import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Category, DirectoryLocation } from "@/types/directory";

interface SearchBarProps {
  categories: Category[];
  districts: DirectoryLocation[];
  defaultKeyword?: string;
  defaultCategory?: string;
  defaultDistrict?: string;
  variant?: "hero" | "inline";
}

export default function SearchBar({
  categories,
  districts,
  defaultKeyword = "",
  defaultCategory = "",
  defaultDistrict = "",
  variant = "hero",
}: SearchBarProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState(defaultKeyword);
  const [category, setCategory] = useState(defaultCategory);
  const [district, setDistrict] = useState(defaultDistrict);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (keyword.trim()) params.set("keyword", keyword.trim());
    if (category) params.set("category", category);
    if (district) params.set("district", district);
    navigate(`/directory?${params.toString()}`);
  };

  const isHero = variant === "hero";
  const selectClass =
    "h-12 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400";

  return (
    <form
      onSubmit={handleSubmit}
      className={`grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:grid-cols-[1.6fr_1fr_1fr_auto] md:items-center ${
        isHero ? "bg-background-50/95 backdrop-blur" : ""
      }`}
    >
      <div className="relative">
        <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
          <i className="ri-search-line text-base" />
        </span>
        <input
          type="text"
          name="keyword"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder="Search business, service or keyword..."
          className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <select
        name="category"
        value={category}
        onChange={(event) => setCategory(event.target.value)}
        className={selectClass}
        aria-label="Select a category"
      >
        <option value="">All Categories</option>
        {categories.map((item) => (
          <option key={item.slug} value={item.slug}>
            {item.name}
          </option>
        ))}
      </select>

      <select
        name="district"
        value={district}
        onChange={(event) => setDistrict(event.target.value)}
        className={selectClass}
        aria-label="Select a district"
      >
        <option value="">All Districts</option>
        {districts.map((item) => (
          <option key={item.slug} value={item.slug}>
            {item.name}
          </option>
        ))}
      </select>

      <button
        type="submit"
        className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-search-line text-base" />
        </span>
        Search
      </button>
    </form>
  );
}